/**
 * `/n/:code`: a nest invite (docs/mockups/nest-prototype.html, "The invite page"). The
 * challenge landing's shape: "‹ Today" and ≡ in the masthead (a first-time invitee has a
 * way home), a printed card naming who invited you, one sentence, your name, and "Join and
 * play today's Easy". After joining, today's puzzle opens at the level you last played
 * (else Easy) if it isn't solved yet, its poster then showing your place in the nest; with
 * every level solved, the Nest tab (nests/model.ts `afterJoinStop`).
 *
 * Works for anyone with the link, dev mode or not (the nests gate, nests/store.ts, opens
 * once this device is in a nest). Names come from the api and are only ever rendered as
 * text. Never a time on this page.
 */
import { type DateKey } from '@arrow/game-engine';
import { type FormEvent, useEffect, useRef, useState } from 'react';

import { type DailyLevels, fetchDailyLevels } from '../api/client.ts';
import { setPageTitle } from '../app/page-title.ts';
import { createPrefs, type Prefs } from '../app/prefs.ts';
import { goUp, navigate } from '../app/router.tsx';
import { AppMenu } from '../components/AppMenu.tsx';
import { BackPill } from '../components/BackPill.tsx';
import { busyButton } from '../components/busy.ts';
import { Wedge } from '../components/icons.tsx';
import { Nib } from '../components/Nib.tsx';
import { ErrorState, Pending, Skeleton, StateBody, StatePage } from '../components/States.tsx';
import { fetchInvite, fetchMyNests, type InviteView, joinNest, NestError } from '../nests/api.ts';
import { afterJoinStop, joinedLine, LEVEL_LABEL } from '../nests/model.ts';
import { noteJoined, rememberNest, rememberNests, setCurrentNest, useNestMemory } from '../nests/store.ts';
import { noteNestJoined } from '../push/push.ts';
import { retryPendingSolves } from '../platform/sync.ts';
import { createPersistence, type Persistence, slotFor } from '../storage/persistence.ts';
import { DISPLAY_NAME_MAX, Field, FormError, nameProblem, nestErrorField, nestErrorLine } from './nest/parts.tsx';
import './nest/nest.css';

type Load = { status: 'loading' } | { status: 'gone' } | { status: 'error' } | { status: 'ready'; invite: InviteView };

export function NestInvite({
	code,
	by,
	todayKey,
	persistence: injected,
	prefs: injectedPrefs,
}: {
	code: string;
	by: string | null;
	todayKey: DateKey;
	/** Tests; the app's own storage otherwise (which of today's levels are solved). */
	persistence?: Persistence;
	/** Tests; the level you last played otherwise. */
	prefs?: Prefs;
}) {
	const [load, setLoad] = useState<Load>({ status: 'loading' });
	const [attempt, setAttempt] = useState(0);
	const memory = useNestMemory();
	const [name, setName] = useState(memory.name ?? '');
	const [error, setError] = useState<{ text: string; field: boolean } | null>(null);
	const [busy, setBusy] = useState(false);
	// Today's levels, to say which one joining opens. Not needed to join: without them, the Nest tab.
	const [day, setDay] = useState<DailyLevels | null>(null);
	const [storage] = useState(() => ({ persistence: injected ?? createPersistence(), prefs: injectedPrefs ?? createPrefs() }));
	const nameRef = useRef<HTMLInputElement>(null);

	useEffect(() => {
		const ctrl = new AbortController();
		setLoad({ status: 'loading' });
		// Today's levels come with the invite, so the button names the level from the start.
		const levels = fetchDailyLevels(todayKey, ctrl.signal).catch(() => null);
		Promise.all([fetchInvite(code, by, ctrl.signal), levels]).then(
			([invite, d]) => {
				if (ctrl.signal.aborted) return;
				setDay(d);
				setLoad({ status: 'ready', invite });
				setPageTitle(`Join ${invite.name}`);
			},
			(err: unknown) => {
				if (ctrl.signal.aborted) return;
				const gone = err instanceof NestError && err.kind === 'not-found';
				setLoad(gone ? { status: 'gone' } : { status: 'error' });
				if (gone) setPageTitle('That invite link doesn’t work');
			},
		);
		return () => ctrl.abort();
	}, [code, by, todayKey, attempt]);

	// After a failed join about your name, focus goes back to the field (A11Y-8).
	useEffect(() => {
		if (error?.field) nameRef.current?.focus({ preventScroll: true });
	}, [error]);

	const stop = day
		? afterJoinStop(
				day.levels.map((l) => l.level),
				storage.prefs.lastLevel(),
				(level) => storage.persistence.resumeInfo(slotFor({ kind: 'daily', date: day.date, level })).status === 'completed',
			)
		: ({ to: 'nest' } as const);

	const join = async (e: FormEvent) => {
		e.preventDefault();
		if (busy || load.status !== 'ready') return;
		const problem = nameProblem(name, DISPLAY_NAME_MAX, 'you');
		if (problem) return setError({ text: problem, field: true });
		setBusy(true);
		setError(null);
		try {
			const nest = await joinNest({ code: load.invite.code, displayName: name.trim(), date: todayKey });
			rememberNest({ id: nest.id, name: nest.name }, name.trim());
			// The next page says you're in (the game's clue band, or the Nest tab): QA2-13.
			noteJoined(nest.name);
			// Its notifications moment waits for the Nest tab (push/Moments.tsx).
			noteNestJoined(nest.name);
			// Solves held back by "Count my plays" can go now: the nest sees them.
			void retryPendingSolves();
			if (stop.to === 'daily' && day) navigate(`/daily/${day.number}?l=${stop.level}`, { replace: true });
			else navigate('/nest', { replace: true });
		} catch (err) {
			setError({ text: nestErrorLine(err, 'join'), field: nestErrorField(err) === 'you' });
			setBusy(false);
		}
	};

	/** Already in it: refresh this device's list, then the Nest tab. */
	const open = async () => {
		try {
			const nests = await fetchMyNests();
			rememberNests(nests);
			const match = load.status === 'ready' ? nests.find((n) => n.name === load.invite.name) : undefined;
			if (match) setCurrentNest(match.id);
		} catch {
			// The tab refreshes itself.
		}
		navigate('/nest', { replace: true });
	};

	if (load.status === 'error')
		return (
			<ErrorState
				error={{ kind: 'network', message: '' }}
				what="this invite"
				onRetry={() => setAttempt((n) => n + 1)}
				link={{ label: 'Play today’s Sparrow', onClick: () => goUp('/') }}
				back="/"
				screen="invite"
			/>
		);

	if (load.status === 'gone')
		return (
			<StatePage back="/" screen="invite" testId="invite-gone">
				<StateBody line="That invite link doesn’t work." sub="It may have been mistyped, or the nest has a new code. Ask for a fresh link.">
					<div className="state-actions">
						<button type="button" className="btn lg primary fit" onClick={() => goUp('/')}>
							Play today’s Sparrow
							<Wedge direction="right" />
						</button>
					</div>
				</StateBody>
			</StatePage>
		);

	// "‹ Today" top left, ≡ top right: the challenge landing's masthead (NJ-8).
	const mast = (
		<header className="page-mast ch-mast">
			<BackPill to="/" />
			<AppMenu screen="invite" install={{ todayKey }} />
		</header>
	);

	if (load.status === 'loading')
		return (
			<div className="page nest-invite landing">
				<main className="page-body page-scroll" aria-busy="true">
					{mast}
					<Pending label="Loading the invite">
						<Skeleton className="invite-skel" />
					</Pending>
				</main>
			</div>
		);

	const inv = load.invite;
	// Everyone else who's in: the person who invited you is named just above (and a nest of one is only them).
	const others = inv.invitedBy ? inv.members.filter((n) => n !== inv.invitedBy) : inv.members;
	const who = joinedLine(others, inv.memberCount - (inv.members.length - others.length));
	const action = stop.to === 'daily' ? `Join and play today’s ${LEVEL_LABEL[stop.level]}` : `Join ${inv.name}`;
	return (
		<div className="page nest-invite landing" data-testid="nest-invite">
			<main className="page-body page-scroll">
				{mast}
				<section className="invite-card" aria-labelledby="inv-h">
					<p className="invite-who">
						{inv.invitedBy ? `${inv.invitedBy} has invited you to join` : 'You’re invited to join'}
						<Nib pose="happy" className="nb" />
					</p>
					<h1 id="inv-h">{inv.name}</h1>
					{who && <p className="invite-sub">{who}</p>}
				</section>
				{inv.joined ? (
					<>
						<p className="land-p">You’re already in {inv.name}.</p>
						<button type="button" className="btn lg primary land-go" onClick={() => void open()}>
							Open {inv.name}
							<Wedge direction="right" />
						</button>
					</>
				) : inv.full ? (
					<>
						<p className="land-p">{inv.name} is full: a nest holds up to 30 people.</p>
						<button type="button" className="btn lg primary land-go" onClick={() => goUp('/')}>
							Play today’s Sparrow
							<Wedge direction="right" />
						</button>
					</>
				) : (
					<form className="land-form" onSubmit={(e) => void join(e)} noValidate>
						<p className="land-p">
							Sparrow is a daily Arrowword. Everyone in {inv.name} plays the same puzzles and sees each other’s times.
						</p>
						<Field
							id="inv-name"
							label="Your name"
							value={name}
							onChange={setName}
							max={DISPLAY_NAME_MAX}
							autoComplete="given-name"
							enterKeyHint="go"
							inputRef={nameRef}
							error={error?.field ? error.text : null}
						/>
						<FormError text={error && !error.field ? error.text : null} />
						<button type="submit" className="btn lg primary land-go" {...busyButton(busy, () => {})}>
							{action}
							<Wedge direction="right" />
						</button>
						<p className="land-fine">Free, no sign-up. Only people in {inv.name} see your name.</p>
					</form>
				)}
			</main>
		</div>
	);
}
